/**
 * Browser WebSocket URL for the compiler and CRDT sockets.
 *
 * The path stays the compiler path (`/compiler/api/v1/ws/...` or
 * `/compiler/api/v1/crdt/ws/...`). The scheme and host stay on the page:
 * `http://humblebucks.com:9003` becomes `ws://humblebucks.com:9003/...`.
 * Ports 9000, 9001, and 9004 are not browser destinations.
 *
 * A public host is never rewritten to COMPILER_WS_URL. That rewrite runs only
 * for a loopback page during `next dev`, which has no upgrade proxy of its own.
 * When the upgrade reaches this Next.js process, `next.config.mjs` beforeFiles
 * rewrites proxy the two socket paths to the compiler on port 9000.
 */

export interface WsPageLocation {
  protocol: string
  host: string
}

const COMPILER_PREFIX = "/compiler"

/** Paths the dev server may hand straight to COMPILER_WS_URL. */
const DEV_DIRECT_SOCKET_PREFIXES = ["/compiler/api/v1/ws/", "/compiler/api/v1/crdt/ws/"]

export type PreparedCompilerSocket =
  | { ok: true; url: string }
  | { ok: false; message: string }

export function isLoopbackHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "")
  return host === "localhost" || host === "127.0.0.1" || host === "::1"
}

export function isAllowedCompilerSocketPath(pathname: string): boolean {
  return DEV_DIRECT_SOCKET_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/** Drop query and fragment so logs cannot include a handshake token. */
export function redactWsUrl(raw: string): string {
  try {
    const url = new URL(raw)
    url.search = ""
    url.hash = ""
    return url.toString()
  } catch {
    return "[invalid websocket url]"
  }
}

/**
 * `next dev` does not upgrade WebSockets. On a loopback page in development,
 * swap the host for COMPILER_WS_URL so a local browser can reach the compiler.
 * A public host such as humblebucks.com stays on the page origin in every
 * environment. COMPILER_WS_URL stays server-only; it is not read by client code.
 */
export function rewriteDevCompilerSocketUrl(
  browserUrl: string,
  compilerWsUrl: string | undefined,
  nodeEnv: string,
): string {
  if (nodeEnv === "production") return browserUrl
  const upstream = compilerWsUrl?.trim()
  if (!upstream) return browserUrl
  try {
    const incoming = new URL(browserUrl)
    if (!isAllowedCompilerSocketPath(incoming.pathname)) return browserUrl
    if (!isLoopbackHostname(incoming.hostname)) return browserUrl
    const target = new URL(upstream)
    target.pathname = incoming.pathname
    target.search = incoming.search
    target.hash = ""
    return target.toString()
  } catch {
    return browserUrl
  }
}

/**
 * Validate the POST body before a token is attached.
 * The host must be one the browser actually used for this request.
 */
export function prepareCompilerSocketUrl(
  rawUrl: string,
  requestHosts: string[],
  compilerWsUrl: string | undefined,
  nodeEnv: string,
): PreparedCompilerSocket {
  let incoming: URL
  try {
    incoming = new URL(rawUrl)
  } catch {
    return { ok: false, message: "Invalid WebSocket URL" }
  }
  if (incoming.username || incoming.password) {
    return { ok: false, message: "WebSocket URL must not include credentials" }
  }
  if (incoming.protocol !== "ws:" && incoming.protocol !== "wss:") {
    return { ok: false, message: "WebSocket URL scheme is not allowed" }
  }
  if (!isAllowedCompilerSocketPath(incoming.pathname)) {
    return { ok: false, message: "WebSocket URL path is not allowed" }
  }
  const allowedHosts = new Set(
    requestHosts.map((host) => host.trim().toLowerCase()).filter(Boolean),
  )
  if (!allowedHosts.has(incoming.host.toLowerCase())) {
    return { ok: false, message: "WebSocket URL host is not allowed" }
  }
  incoming.hash = ""
  return {
    ok: true,
    url: rewriteDevCompilerSocketUrl(incoming.toString(), compilerWsUrl, nodeEnv),
  }
}

/**
 * Choose the URL passed to `new WebSocket()`.
 * A public page never follows a resolver response onto another host.
 * The token from that response is kept on the page-origin URL.
 * Loopback development may still use the compiler host returned for `next dev`.
 */
export function selectBrowserSocketUrl(
  requestedUrl: string,
  resolvedUrl: string | undefined,
): { url: string; rejectedForeignHost: boolean } {
  if (!resolvedUrl) return { url: requestedUrl, rejectedForeignHost: false }
  let requested: URL
  let resolved: URL
  try {
    requested = new URL(requestedUrl)
    resolved = new URL(resolvedUrl)
  } catch {
    return { url: requestedUrl, rejectedForeignHost: false }
  }
  const schemeOk = resolved.protocol === "ws:" || resolved.protocol === "wss:"
  if (!schemeOk || !isAllowedCompilerSocketPath(resolved.pathname)) {
    return { url: requestedUrl, rejectedForeignHost: false }
  }
  if (isLoopbackHostname(requested.hostname)) {
    return { url: resolved.toString(), rejectedForeignHost: false }
  }
  const sameTarget = resolved.host.toLowerCase() === requested.host.toLowerCase()
    && resolved.protocol === requested.protocol
  if (sameTarget) return { url: resolved.toString(), rejectedForeignHost: false }
  const token = resolved.searchParams.get("token")
  if (token) requested.searchParams.set("token", token)
  return {
    url: requested.toString(),
    rejectedForeignHost: resolved.host.toLowerCase() !== requested.host.toLowerCase(),
  }
}

export function toSameOriginCompilerWsUrl(raw: string, page?: WsPageLocation): string {
  const loc = page ?? (typeof window !== "undefined"
    ? { protocol: window.location.protocol, host: window.location.host }
    : null)
  if (!loc?.host) {
    throw new Error("Compiler WebSocket URL requires a browser location")
  }

  const wsProtocol = loc.protocol === "https:" ? "wss:" : "ws:"
  let pathname: string
  let search: string

  if (/^(wss?|https?):\/\//i.test(raw)) {
    const parsed = new URL(raw)
    pathname = parsed.pathname || "/"
    search = parsed.search
  } else {
    const queryAt = raw.indexOf("?")
    const pathPart = queryAt >= 0 ? raw.slice(0, queryAt) : raw
    search = queryAt >= 0 ? raw.slice(queryAt) : ""
    pathname = pathPart.startsWith("/") ? pathPart : `/${pathPart}`
  }

  // COMPILER_API_URL is mounted at /compiler. Relative session paths are
  // `/api/v1/ws/...`; some absolute URLs omit that prefix. The public path
  // always includes it so it does not collide with this app's `/api` routes.
  if (pathname !== COMPILER_PREFIX && !pathname.startsWith(`${COMPILER_PREFIX}/`)) {
    pathname = `${COMPILER_PREFIX}${pathname}`
  }

  return `${wsProtocol}//${loc.host}${pathname}${search}`
}
